import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  ActionPortal,
  ButtonWithIcon,
  Checkbox,
  DefaultActionButtons,
  Icon,
  MaskedInput,
  Step,
  Switch,
  Text,
} from 'components';

import { Gray, Orange, Purple } from 'constants/colors';
import HelpIconWithTooltip from 'components/helpIconWithTooltip';
import { StepPropTypes } from 'constants/propTypes';

export default function Blocker(props) {
  const {
    theme,
    onSubmit,
    onCancel,
    initialValues: { blocker, vehicleManager, quantityTypes } = {},
  } = props;

  const {
    control,
    errors,
    handleSubmit,
    register,
    watch,
    setValue,
  } = useForm({
    mode: 'all',
    defaultValues: {
      useBlocker: blocker?.useBlocker || false,
      twelveVoltsCount: blocker?.twelveVoltsCount || 0,
      twentyFourVoltsCount: blocker?.twentyFourVoltsCount || 0,
      acceptTerms: blocker?.acceptTerms || false,
    },
  });

  const useBlocker = watch('useBlocker');
  const optInSupport = watch('acceptTerms');
  const twelveVoltsCount = watch('twelveVoltsCount');
  const twentyFourVoltsCount = watch('twentyFourVoltsCount');

  const maxBlockerQuantity = 
    (quantityTypes?.tracker2GCount || 0) + 
    (quantityTypes?.tracker4GCount || 0) +
    (vehicleManager?.conteleCamerasCount || 0);

  const totalBlockerCount = (twelveVoltsCount || 0) + (twentyFourVoltsCount || 0);

  const canProceed = !useBlocker || (useBlocker && totalBlockerCount > 0 && optInSupport);
  const canAddMore = totalBlockerCount < maxBlockerQuantity;

  useEffect(() => {
    if (!useBlocker) {
      setValue('twelveVoltsCount', 0);
      setValue('twentyFourVoltsCount', 0);
      setValue('acceptTerms', false);
    }
  }, [useBlocker, setValue]);

  return (
    <div style={{ height: '100%', overflow: 'hidden', backgroundColor: Gray[200] }}>
      <Step
        headerTitle="Bloqueio do Veículo (opcional)"
        style={{
          backgroundColor: Gray[200], 
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <form
          onKeyPress={(e) => e.key === 'Enter' && handleSubmit(onSubmit)()}
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 16,
            alignItems: 'center',
          }}
        >
          <section
            style={{
              padding: '30px 16px',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 5,
              border: 'solid 1px',
              height: '100%',
              backgroundColor: Gray[100],
              borderColor: `${theme.newPalette.background.gray[100]}33`,
            }}
          >
            <article
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
              }}
            >
              <h1
                style={{
                  fontSize: 30,
                  fontWeight: 600,
                }}
              >
                Bloqueio de combustível
              </h1>
              <p
                style={{
                  fontSize: 16,
                  color: theme.newPalette.typography[300],
                }}
              >
                Existe um opcional no Contele Fleet, onde é possível bloquear o veículo através de um Relê automotivo. Essa facilidade tem um custo adicional e só é possível para rastreadores com fio.
              </p>
            </article>
            <section
              style={{
                padding: '20px 18px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 8,
                marginTop: 26,
                border: 'solid 2px',
                width: '100%',
                borderColor: useBlocker
                  ? theme.newPalette.background.purple
                  : `${theme.newPalette.background.gray[100]}88`,
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -18,
                  backgroundColor: '#FFFFFF',
                  border: 'solid 2px',
                  borderRadius: 64,
                  borderColor: `${theme.newPalette.background.gray[100]}44`,
                  padding: '4px 0px 4px 10px',
                }}
              >
                <Switch
                  inline
                  innerRef={register}
                  type="switch"
                  id="useBlocker"
                  name="useBlocker"
                  label="Quero bloqueio"
                />
              </div>
              <div style={{ display: 'flex', width: '100%' }}>
                <article
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '30%',
                    justifyContent: 'center',
                  }}
                >
                  <div>
                    <div style={{ marginTop: 8 }}>
                      <p>
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 26,
                            marginRight: '8px',
                          }}
                        >
                          R$3,00
                        </span>
                        <span style={{ opacity: 0.8 }}>/mês por veículo</span>
                      </p>
                    </div>
                  </div>
                </article>
                <div
                  style={{
                    height: '80px',
                    alignSelf: 'center',
                    width: '2px',
                    backgroundColor: `${theme.newPalette.background.gray[100]}44`,
                  }}
                />
                <article
                  style={{
                    display: 'flex',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      marginTop: 16,
                      padding: '0px 26px',
                      flexDirection: 'column',
                      width: '100%',
                      gap: 6,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Text
                        color={Gray[400]}
                        fontSize="14px"
                        lineHeight="17px"
                        fontWeight="500"
                      >
                        Relê de corte 12 Volts
                      </Text>
                      <HelpIconWithTooltip
                        text="Relê de corte de combustível 12 Volts normalmente usado em carros de passeio."
                        color={Gray[300]}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                      <ButtonWithIcon
                        icon="minus"
                        customBackgroundColor={Gray[100]}
                        border={`1px solid ${Gray[250]}`}
                        customIconColor={Gray[400]}
                        height="32px"
                        iconOptions={{ width: '14px', height: '14px' }}
                        onClick={() => {
                          if (twelveVoltsCount === 0) return;

                          setValue(
                            'twelveVoltsCount',
                            (twelveVoltsCount || 0) - 1,
                          );
                        }}
                        disabled={!useBlocker}
                      />
                      <MaskedInput
                        control={control}
                        errors={errors}
                        name="twelveVoltsCount"
                        disabled={!useBlocker}
                        placeholder="0"
                        format="numeric"
                        maxLength="3"
                        min={0}
                      />
                      <ButtonWithIcon
                        icon="plus"
                        customBackgroundColor={Gray[100]}
                        border={`1px solid ${Gray[250]}`}
                        customIconColor={Gray[400]}
                        height="32px"
                        iconOptions={{ width: '14px', height: '14px' }}
                        onClick={() => {
                          if (!canAddMore) return;

                          setValue(
                            'twelveVoltsCount',
                            (twelveVoltsCount || 0) + 1,
                          );
                        }}
                        disabled={!useBlocker || !canAddMore}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      marginTop: 16,
                      padding: '0px 26px',
                      flexDirection: 'column',
                      width: '100%',
                      gap: 6,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Text
                        color={Gray[400]}
                        fontSize="14px"
                        lineHeight="17px"
                        fontWeight="500"
                      >
                        Relê de corte 24 Volts
                      </Text>
                      <HelpIconWithTooltip
                        text="Relê de corte de combustível 24 Volts normalmente usado em veículos maiores, como caminhões e ônibus."
                        color={Gray[300]}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                      <ButtonWithIcon
                        icon="minus"
                        customBackgroundColor={Gray[100]}
                        border={`1px solid ${Gray[250]}`}
                        customIconColor={Gray[400]}
                        height="32px"
                        iconOptions={{ width: '14px', height: '14px' }}
                        onClick={() => {
                          if (twentyFourVoltsCount === 0) return;

                          setValue(
                            'twentyFourVoltsCount',
                            (twentyFourVoltsCount || 0) - 1,
                          );
                        }}
                        disabled={!useBlocker}
                      />
                      <MaskedInput
                        control={control}
                        errors={errors}
                        name="twentyFourVoltsCount"
                        disabled={!useBlocker}
                        placeholder="0"
                        format="numeric"
                        maxLength="3"
                        min={0}
                      />
                      <ButtonWithIcon
                        icon="plus"
                        customBackgroundColor={Gray[100]}
                        border={`1px solid ${Gray[250]}`}
                        customIconColor={Gray[400]}
                        height="32px"
                        iconOptions={{ width: '14px', height: '14px' }}
                        onClick={() => {
                          if (!canAddMore) return;

                          setValue(
                            'twentyFourVoltsCount',
                            (twentyFourVoltsCount || 0) + 1,
                          );
                        }}
                        disabled={!useBlocker || !canAddMore}
                      />
                    </div>
                  </div>
                </article>
              </div>
              {useBlocker && (
                <div
                  style={{
                    padding: '14px 16px',
                    display: 'flex',
                    width: '100%',
                    borderRadius: 8,
                    backgroundColor: Gray[200],
                    marginTop: 22,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Icon
                        color={Gray[400]}
                        icon="exclamationCircle"
                        style={{ width: '16px', height: '16px' }}
                      />
                      <Text
                        color={Gray[400]}
                        fontSize="16px"
                        lineHeight="19px"
                        fontWeight="700"
                      >
                        Alerta Importante: Saiba os Riscos do Bloqueio Veicular
                      </Text>
                    </div>
                    <ul
                      style={{
                        fontSize: 14,
                        gap: 12,
                        marginLeft: 25,
                        marginBottom: 20,
                        opacity: 0.8,
                        color: Gray[400],
                      }}
                    >
                      Optar pelo bloqueio veicular vem com riscos que é crucial você
                      entender:
                      <li style={{ marginLeft: 12, display: 'flex', gap: 12 }}>
                        <div style={{ fontSize: 26 }}>•</div>
                        {' '}
                        A instalação requer
                        cortes nos fios, o que provavelmente causará a perda da
                        garantia do veículo.
                      </li>
                      <li style={{ marginLeft: 12, display: 'flex', gap: 12 }}>
                        <div style={{ fontSize: 26 }}>•</div>
                        {' '}
                        O bloqueio ou
                        desbloqueio indevidos do veículo são de sua total
                        responsabilidade e podem causar danos ao cliente ou
                        terceiros.
                      </li>
                      <li style={{ marginLeft: 12, display: 'flex', gap: 12 }}>
                        <div style={{ fontSize: 26 }}>•</div>
                        {' '}
                        Reforçamos que
                        prejuízos à sua empresa podem ocorrer se essas questões não
                        forem bem geridas.
                      </li>
                      <li style={{ marginLeft: 12, display: 'flex', gap: 12 }}>
                        <div style={{ fontSize: 26 }}>•</div>
                        {' '}
                        A Contele só recomenda
                        esta opção para quem estiver plenamente ciente e aceitar
                        esses riscos.
                      </li>
                      <li style={{ marginLeft: 12, fontWeight: 600 }}>
                        Não assumimos responsabilidade por consequências adversas
                      </li>
                    </ul>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        type="checkbox"
                        control={control}
                        name="acceptTerms"
                        id="acceptTerms"
                        innerRef={register({
                          required: useBlocker,
                        })}
                        disabled={!useBlocker}
                      />
                      <Text
                        color={Gray[400]}
                        fontSize="14px"
                        lineHeight="19px"
                        fontWeight="400"
                        textAlign="center"
                      >
                        Sim, estou ciente.
                      </Text>
                    </div>
                  </div>
                </div>
              )}
              
              {useBlocker && !canProceed && (
                <div
                  style={{
                    padding: '14px 16px',
                    display: 'flex',
                    width: '100%',
                    borderRadius: 8,
                    backgroundColor: Orange[100],
                    marginTop: 12,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Icon
                        color={Orange[400]}
                        icon="exclamationTriangle"
                        style={{ width: '16px', height: '16px' }}
                      />
                      <Text
                        color={Orange[400]}
                        fontSize="16px"
                        lineHeight="19px"
                        fontWeight="700"
                      >
                        Requisitos para Prosseguir
                      </Text>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                    >
                      {useBlocker && totalBlockerCount === 0 && (
                        <Text
                          color={Orange[400]}
                          fontSize="14px"
                          lineHeight="19px"
                          fontWeight="400"
                        >
                          • Você precisa selecionar pelo menos um bloqueio (12V ou 24V)
                        </Text>
                      )}
                      {useBlocker && !optInSupport && (
                        <Text
                          color={Orange[400]}
                          fontSize="14px"
                          lineHeight="19px"
                          fontWeight="400"
                        >
                          • É necessário aceitar os termos sobre os riscos do bloqueio veicular
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </section>

          <ActionPortal>
            <DefaultActionButtons
              theme={theme}
              isValid={canProceed}
              backButtonStyle={{
                iconPosition: 'left',
                border: 'none',
                customBackgroundColor: 'transparent',
                customIconColor: Gray[400],
                customTextColor: Gray[400],
                title: 'Passo Anterior',
              }}
              nextButtonStyle={{
                customBackgroundColor: Purple[500],
                customIconColor: Gray[100],
                customTextColor: Gray[100],
                title: 'Próximo Passo',
              }}
              onCancel={onCancel}
              onSubmit={() => handleSubmit(onSubmit)()}
            />
          </ActionPortal>
        </form>
      </Step>
    </div>
  );
}

Blocker.propTypes = StepPropTypes;
